// The SELECTED files — the attached half of the Work-board (busy-my-web#444 / #445).
//
// A file that has been put on the Work-board is UPLOADED; a file the user has then picked (clicked
// its chip, or dragged it onto the composer) is ATTACHED. The two states are one flag apart in
// SmartBox.vue — `quickFiles` holds everything staged, `attachedFileIds` the picked subset — and the
// whole of #445 is about WHERE each may be seen:
//
//   uploaded, not picked → the Work-board only, as the white chip (frame 40008539:201115)
//   attached            → the Work-board's tinted chip ("· selected") and the creation item on
//                         the Timeline rail; the Briefing reports the set as a fact ("1 JPG
//                         selected", lib/attachedSummary.js)
//
// The Briefing's own "Selected files" section (#444) was removed by Pouria, 2026-10-07 (#585). The
// surfaces that still draw the attached list read it here rather than each over again: what a chip
// is called, which glyph it takes, and the one line the rail writes over the rows.
// Plain values, no store and no component — the same shape lib/briefings/* is kept in, so the rule
// can be asked without mounting the box.

import { fileExtension } from '@/lib/briefings/entityFiles'
import { isImageAttachment } from '@/lib/globalTimelineFeed'


/**
 * The word a picked file wears after its name (busy-my-web#585): the Work-board chip writes
 * "· selected", 10px primary.
 * On a CREATION card this is superseded by Figma 40013943:443733 (Pouria 2026-10-14): the board
 * groups files as "N attached to Task" / "N ready to attach" (lib/workboardAttach.js) and no chip
 * carries the word. It remains on the boards that attach without a creation card (an Ongoing's
 * page, a record's reader page).
 */
export const SELECTED_LABEL = 'selected'

/** The rail's own way out of the selection, under the rows (frame 40008539:201115). */
export const CLEAR_SELECTION_LABEL = 'Clear selection'

/**
 * One staged file, ready to draw: `{ id, name, base, ext, isImage, icon, url }`.
 *
 * `base` / `ext` are split because the rail writes the extension as a span of its own; the chips
 * write `name` whole. A file keeps whatever name it has been given — `name` is the computer file's,
 * `filename` the one Drive answered with — and the id is the composer's `tempId`, which is what
 * every attach/detach in the box is keyed by.
 */
export function selectedFileRow(file) {
  if (!file) return null
  const id = file.tempId || file.fileId || file.id || ''
  if (!id) return null
  const name = String(file.name || file.filename || 'Attachment')
  const ext = fileExtension(name)
  const isImage = isImageAttachment({ contentType: file.contentType, extension: ext })
  return {
    id,
    name,
    // The dot stays with the extension span, so the two halves join back into the whole name.
    base: ext ? name.slice(0, name.length - ext.length - 1) : name,
    ext,
    isImage,
    // The frame's own glyph for a file that is not a picture; a picture says so with `gallery`
    // wherever its thumbnail has not been fetched (the rail cards, the chips).
    icon: isImage ? 'gallery' : 'folder-minus',
    // What a picture can be drawn FROM, when the file is already in Drive. A computer file staged
    // in the box has no URL until it is uploaded (that happens on save), so it takes its glyph.
    url: file.webContentLink || '',
  }
}

/** The attached files, in the order they were attached, as rows. */
export function selectedFileRows(files) {
  return (files || []).map(selectedFileRow).filter(Boolean)
}

/**
 * "2 items selected" — the line the rail's creation item writes under "Ready when you are" once
 * something has been attached to the thing being made (frame 40008539:201115).
 *
 * Empty for an empty selection: with nothing attached the item keeps the description it already had
 * ("Task opened", lib/focusedPage.js), which is the recorded wording for that state.
 */
export function selectionCountLine(count) {
  const n = Number(count) || 0
  if (n <= 0) return ''
  return `${n} item${n === 1 ? '' : 's'} selected`
}

/**
 * "3 files selected" — the rail's line while the Recent files page is up and files are picked there
 * (frames 41 / 50: "Ready when you are / 3 files selected"). Empty for none.
 */
export function filesSelectedLine(count) {
  const n = Number(count) || 0
  if (n <= 0) return ''
  return `${n} file${n === 1 ? '' : 's'} selected`
}

/**
 * The Recent files page's picks (lib/recentFilesPage cards) as rail rows — the same row shape as
 * `selectedFileRow`, with the card's own type colour for the glyph (frame 41: the PDF red, the MP3
 * pink) and a picture's gallery glyph on the grey tile.
 */
export function pickedFileRows(cards) {
  return (cards || []).filter(c => c && c.id).map((c) => {
    const row = selectedFileRow({ tempId: String(c.id), name: c.name, contentType: c.contentType })
    return { ...row, icon: row.isImage ? 'gallery' : 'type-file', color: row.isImage ? '#4b5563' : (c.type?.color || '#111827') }
  })
}
